/**
 * Alpha RBAC — Role-Based Access Control helpers
 * ─────────────────────────────────────────────
 * Supports three core permission levels:
 *   1. Teacher        → assigned subjects/classes only
 *   2. Class Teacher  → everything teacher can do + manage assigned class
 *   3. School Admin   → full school access
 *
 * Profiles may carry roles in two shapes:
 *   - profile.role  (legacy single role)
 *   - profile.roles (array of roles)
 *
 * Class assignment sources (checked in order):
 *   - schools/{schoolId}/teacherAssignments/{id}
 *   - class document fields: classTeacherId, teacherId, teacherIds,
 *     assignedTeacherIds, activeTeacherUids, classTeacherIds
 */

import { useEffect, useMemo, useState } from "react";
import { getFirebaseDb, collection, query, where, onSnapshot, orderBy } from "./alphaFirebase.js";
import { normalizeAlphaRoles } from "./alphaRoles.js";

const SCHOOL_ID = "alpha";

export const RBAC_ROLES = {
  TEACHER: "teacher",
  SUBJECT_TEACHER: "subject_teacher",
  CLASS_TEACHER: "class_teacher",
  SCHOOL_ADMIN: "schoolAdmin",
  SUPER_ADMIN: "superAdmin",
  STUDENT: "student",
  PARENT: "parent",
};

function normalizeRoleList(profile = {}) {
  const roles = normalizeAlphaRoles(profile);
  const role = String(profile?.role || "").toLowerCase();
  const all = Array.from(new Set([...roles, role].filter(Boolean)));
  return all;
}

export function hasRole(profile, role) {
  if (!profile || !role) return false;
  const roles = normalizeRoleList(profile);
  const target = String(role).toLowerCase();
  if (target === RBAC_ROLES.TEACHER) {
    return roles.some((r) => [RBAC_ROLES.TEACHER, RBAC_ROLES.SUBJECT_TEACHER, RBAC_ROLES.CLASS_TEACHER].includes(r));
  }
  if (target === RBAC_ROLES.CLASS_TEACHER) {
    return roles.includes(RBAC_ROLES.CLASS_TEACHER);
  }
  if (target === RBAC_ROLES.SCHOOL_ADMIN) {
    return roles.some((r) => [RBAC_ROLES.SCHOOL_ADMIN, "school_admin", RBAC_ROLES.SUPER_ADMIN, "super_admin"].includes(r));
  }
  return roles.includes(target);
}

export function isSuperAdmin(profile) {
  return hasRole(profile, RBAC_ROLES.SUPER_ADMIN);
}

export function isSchoolAdmin(profile) {
  return hasRole(profile, RBAC_ROLES.SCHOOL_ADMIN) || isSuperAdmin(profile);
}

export function isTeacher(profile) {
  return hasRole(profile, RBAC_ROLES.TEACHER);
}

export function isClassTeacher(profile) {
  return hasRole(profile, RBAC_ROLES.CLASS_TEACHER);
}

export function isStudent(profile) {
  return hasRole(profile, RBAC_ROLES.STUDENT);
}

/**
 * Return the effective role for UI/permissions.
 * Honors a "View as" preview role when an admin is testing.
 */
export function getEffectiveRole(profile, viewAsRole = null) {
  if (viewAsRole) return viewAsRole;
  if (isSuperAdmin(profile)) return RBAC_ROLES.SCHOOL_ADMIN;
  if (isSchoolAdmin(profile)) return RBAC_ROLES.SCHOOL_ADMIN;
  if (isClassTeacher(profile)) return RBAC_ROLES.CLASS_TEACHER;
  if (isTeacher(profile)) return RBAC_ROLES.TEACHER;
  return profile?.role || "student";
}

/**
 * Collect all teacher UIDs associated with a class document.
 *
 * Supports the canonical Alpha class fields:
 *   - assignedTeacherUid / assignedTeacherName / assignedTeacherEmail
 *   - classTeacherUid   / classTeacherName   / classTeacherEmail
 *   - createdByUid      / createdByName      / createdByEmail
 * plus legacy aliases for backward compatibility.
 */
export function classTeacherUids(classItem = {}) {
  const uids = [
    classItem.assignedTeacherUid,
    classItem.classTeacherUid,
    classItem.createdByUid,
    classItem.createdBy,
    classItem.ownerId,
    classItem.teacherId,
    classItem.classTeacherId,
    ...(Array.isArray(classItem.teacherIds) ? classItem.teacherIds : []),
    ...(Array.isArray(classItem.assignedTeacherIds) ? classItem.assignedTeacherIds : []),
    ...(Array.isArray(classItem.activeTeacherUids) ? classItem.activeTeacherUids : []),
    ...(Array.isArray(classItem.classTeacherIds) ? classItem.classTeacherIds : []),
  ];
  return Array.from(new Set(uids.filter(Boolean)));
}

export function classAssignedTeacherUid(classItem = {}) {
  return classItem.assignedTeacherUid || classItem.teacherId || classItem.assignedTeacherIds?.[0] || "";
}

export function classClassTeacherUid(classItem = {}) {
  return classItem.classTeacherUid || classItem.classTeacherId || classItem.classTeacherIds?.[0] || "";
}

export function classCreatorUid(classItem = {}) {
  return classItem.createdByUid || classItem.createdBy || classItem.ownerId || "";
}

/**
 * Build a lookup of classId → { isClassTeacher, isSubjectTeacher, subjects }
 * from the teacherAssignments collection.
 */
export function buildTeacherAssignmentMap(teacherAssignments = [], teacherUid = "") {
  const map = new Map();
  for (const a of teacherAssignments) {
    if (!a || a.teacherId !== teacherUid || a.status === "deleted") continue;
    const existing = map.get(a.classId) || { isClassTeacher: false, isSubjectTeacher: false, subjects: [] };
    if (a.isClassTeacher === true) existing.isClassTeacher = true;
    if (a.subjectId || a.subjectName) {
      existing.isSubjectTeacher = true;
      existing.subjects.push(a.subjectName || a.subjectId);
    }
    // If no explicit subject but assignment exists, treat as subject teacher
    if (!a.isClassTeacher && !a.subjectId && !a.subjectName) {
      existing.isSubjectTeacher = true;
    }
    map.set(a.classId, existing);
  }
  return map;
}

/**
 * Filter classes according to the user's effective role.
 *
 * Returns:
 *   {
 *     teachingClasses: classes where user is a subject teacher,
 *     managedClasses:  classes where user is class teacher,
 *     visibleClasses:  union of both (for general visibility),
 *     isAdmin:         true for school/super admin
 *   }
 */
export function filterClassesByRole(classes = [], profile = {}, teacherAssignments = [], viewAsRole = null) {
  const effectiveRole = getEffectiveRole(profile, viewAsRole);
  const uid = profile?.uid || "";

  if (effectiveRole === RBAC_ROLES.SCHOOL_ADMIN || effectiveRole === "superAdmin" || effectiveRole === "school_admin") {
    return {
      teachingClasses: classes,
      managedClasses: classes,
      visibleClasses: classes,
      isAdmin: true,
    };
  }

  const assignmentMap = buildTeacherAssignmentMap(teacherAssignments, uid);
  const teaching = [];
  const managed = [];

  for (const c of classes) {
    const assigned = assignmentMap.get(c.id);
    const uids = classTeacherUids(c);
    const isAssigned = uids.includes(uid);
    const isClassTeacher = assigned?.isClassTeacher || c.classTeacherId === uid || (c.classTeacherIds || []).includes(uid);
    const isSubjectTeacher = assigned?.isSubjectTeacher || isAssigned;

    if (isClassTeacher) managed.push(c);
    else if (isSubjectTeacher) teaching.push(c);
  }

  return {
    teachingClasses: teaching,
    managedClasses: managed,
    visibleClasses: Array.from(new Set([...teaching, ...managed])),
    isAdmin: false,
  };
}

/**
 * Can the user edit/manage a specific class?
 */
export function canManageClass(classItem = {}, profile = {}, teacherAssignments = []) {
  if (isSchoolAdmin(profile) || isSuperAdmin(profile)) return true;
  const uid = profile?.uid || "";
  const assignmentMap = buildTeacherAssignmentMap(teacherAssignments, uid);
  const assigned = assignmentMap.get(classItem.id);
  if (assigned?.isClassTeacher) return true;
  const uids = classTeacherUids(classItem);
  return uids.includes(uid);
}

/**
 * Can the user teach a specific class (subject teacher)?
 */
export function canTeachClass(classItem = {}, profile = {}, teacherAssignments = []) {
  if (isSchoolAdmin(profile) || isSuperAdmin(profile)) return true;
  const uid = profile?.uid || "";
  const assignmentMap = buildTeacherAssignmentMap(teacherAssignments, uid);
  const assigned = assignmentMap.get(classItem.id);
  if (assigned?.isSubjectTeacher || assigned?.isClassTeacher) return true;
  const uids = classTeacherUids(classItem);
  return uids.includes(uid);
}

/**
 * Real-time hook that loads only the classes a user is allowed to see.
 * Admins see all classes; teachers see assigned/managed classes only.
 */
export function useTeacherClasses(profile, viewAsRole = null) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const effectiveRole = useMemo(() => getEffectiveRole(profile, viewAsRole), [profile, viewAsRole]);
  const uid = profile?.uid || "";

  useEffect(() => {
    if (!profile) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const db = getFirebaseDb();
    const col = collection(db, "schools", SCHOOL_ID, "classes");

    // Admin previewing their own admin role sees everything.
    if (effectiveRole === RBAC_ROLES.SCHOOL_ADMIN || effectiveRole === "superAdmin" || effectiveRole === "school_admin") {
      const q = query(col, orderBy("createdAt", "desc"));
      const unsub = onSnapshot(q, (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setError("");
        setLoading(false);
      }, (err) => {
        console.error("[useTeacherClasses] admin load failed", err);
        setError(err.message);
        setLoading(false);
      });
      return unsub;
    }

    // Teachers: load classes where they appear on the class doc or in teacherAssignments.
    // We listen to multiple targeted queries and merge results. This also satisfies
    // Firestore rules that require a teacher-field filter for non-admin reads.
    const unsubscribers = [];
    const byId = new Map();
    const merge = (snap) => {
      snap.docChanges().forEach((change) => {
        if (change.type === "removed") byId.delete(change.doc.id);
        else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
      });
      setItems(Array.from(byId.values()).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
      setLoading(false);
    };

    // Canonical single-value teacher fields take priority.
    const teacherFields = ["assignedTeacherUid", "classTeacherUid", "createdByUid", "createdBy", "ownerId", "teacherId", "classTeacherId"];
    for (const field of teacherFields) {
      const q = query(col, where(field, "==", uid), orderBy("createdAt", "desc"));
      unsubscribers.push(onSnapshot(q, merge, (err) => {
        console.error(`[useTeacherClasses] ${field} listener failed`, err);
        setError(err.message);
      }));
    }

    const arrayFields = ["teacherIds", "assignedTeacherIds", "activeTeacherUids", "classTeacherIds"];
    for (const field of arrayFields) {
      const q = query(col, where(field, "array-contains", uid), orderBy("createdAt", "desc"));
      unsubscribers.push(onSnapshot(q, merge, (err) => {
        console.error(`[useTeacherClasses] ${field} listener failed`, err);
        setError(err.message);
      }));
    }

    // Also load via teacherAssignments (isClassTeacher / subject teacher).
    const assignmentQuery = query(
      collection(db, "schools", SCHOOL_ID, "teacherAssignments"),
      where("teacherId", "==", uid),
      where("status", "==", "active")
    );
    unsubscribers.push(onSnapshot(assignmentQuery, (snap) => {
      const classIds = Array.from(new Set(snap.docs.map((d) => d.data().classId).filter(Boolean)));
      if (classIds.length === 0) {
        setLoading(false);
        return;
      }
      // Firestore "in" supports up to 10 values; chunk if needed.
      const chunkSize = 10;
      for (let i = 0; i < classIds.length; i += chunkSize) {
        const chunk = classIds.slice(i, i + chunkSize);
        const q = query(col, where("__name__", "in", chunk));
        unsubscribers.push(onSnapshot(q, merge, (err) => {
          console.error("[useTeacherClasses] assignment class listener failed", err);
          setError(err.message);
        }));
      }
      setLoading(false);
    }, (err) => {
      console.error("[useTeacherClasses] assignment listener failed", err);
      setError(err.message);
      setLoading(false);
    }));

    return () => {
      for (const unsub of unsubscribers) {
        if (typeof unsub === "function") unsub();
      }
    };
  }, [profile, effectiveRole, uid]);

  return { items, loading, error };
}
